import { z } from 'zod';

// ── Visibility enum ────────────────────────────────────────────────────────
// Mirrors core.file_folders.visibility (migration 0053).
//
// 'inherited' — anyone with project access sees the folder + its contents
// 'restricted' — only listed users + creator + OWNER/MANAGER bypass.  LEAD
//                manages restrict/grant only on folders they created (founder
//                decision 2026-05-17, enforced at the service layer).

export const FolderVisibilitySchema = z.enum(['inherited', 'restricted']);
export type FolderVisibility = z.infer<typeof FolderVisibilitySchema>;

// ── Folder projection ──────────────────────────────────────────────────────
// What every read endpoint returns.  Tree is reconstructed FE-side from
// parentId (flat-list response keeps the API trivially RLS-correct: invisible
// nodes simply don't appear in the list).

export const FolderSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  parentId: z.string().uuid().nullable(),
  name: z.string(),
  visibility: FolderVisibilitySchema,
  createdBy: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Whether the actor created this folder.  Surfaces to the FE so it can
   *  show "you can manage access" hints on LEAD's own folders. */
  isOwnedByMe: z.boolean(),
  /** True for the project's root folder when emitted as a synthetic entry.
   *  Currently always false — kept reserved for a future virtual-root view. */
  isRoot: z.boolean(),
});
export type FolderProjection = z.infer<typeof FolderSchema>;
export const FolderListSchema = z.array(FolderSchema);

// ── Create folder ──────────────────────────────────────────────────────────

export const CreateFolderInputSchema = z.object({
  name: z.string().min(1).max(120),
  parentId: z.string().uuid().nullable().default(null),
  /** Initial visibility.  Defaults to 'inherited' — restrict later via PATCH. */
  visibility: FolderVisibilitySchema.default('inherited'),
});
export type CreateFolderInput = z.infer<typeof CreateFolderInputSchema>;

// ── Update folder (rename / move / change visibility) ──────────────────────
// All fields optional — caller sends only what's changing.
// `parentId` is `null` to move to root; field omission = no move.  Use the
// explicit `moveToRoot` flag on the move endpoint if a future TS issue forces
// the distinction; today Zod treats `undefined` as omitted and `null` as
// explicit root-move (we use `.transform` below to keep both paths safe).

export const UpdateFolderInputSchema = z
  .object({
    name: z.string().min(1).max(120).optional(),
    parentId: z.string().uuid().nullable().optional(),
    visibility: FolderVisibilitySchema.optional(),
  })
  .refine(
    (v) => v.name !== undefined || v.parentId !== undefined || v.visibility !== undefined,
    { message: 'At least one of name, parentId, visibility must be set' },
  );
export type UpdateFolderInput = z.infer<typeof UpdateFolderInputSchema>;

// ── Move file between folders ──────────────────────────────────────────────
// PATCH /api/files/:id  body
//
//   { "folderId": null }      → move file to project root (no folder)
//   { "folderId": "<uuid>" }  → move file into the given folder

export const MoveFileInputSchema = z.object({
  folderId: z.string().uuid().nullable(),
});
export type MoveFileInput = z.infer<typeof MoveFileInputSchema>;

// ── Per-folder ACL ─────────────────────────────────────────────────────────
// Only applies to folders with `visibility = 'restricted'`.  Inherited folders
// are visible to everyone with project access — no ACL row needed.

export const FolderAccessSchema = z.object({
  userId: z.string().uuid(),
  /** Display fields for the FE access list — null when the user was soft-deleted. */
  username: z.string().nullable(),
  name: z.string().nullable(),
  avatarUrl: z.string().url().nullable(),
  grantedBy: z.string().uuid(),
  grantedAt: z.string(),
  /** True when this row was created by the OUTSTAFF auto-grant on upload
   *  (mirrors `project_access` auto-grant in docs/22 §3.3) so the FE can
   *  badge it as "Auto-granted via upload" rather than a manual share.
   *  Detected by `granted_by == user_id` (the actor granted themselves
   *  access through the upload path). */
  isAutoGranted: z.boolean(),
});
export type FolderAccessProjection = z.infer<typeof FolderAccessSchema>;
export const FolderAccessListSchema = z.array(FolderAccessSchema);

export const GrantFolderAccessInputSchema = z.object({
  userId: z.string().uuid(),
});
export type GrantFolderAccessInput = z.infer<typeof GrantFolderAccessInputSchema>;
